// Compiles and runs CPQ operation scripts against a working runtime.
//
// Each operation has:
//   custrecord_cpqc_operation_parsed_rule  - JS expression; op runs when truthy
//   custrecord_cpqc_code_exec              - JS body to execute
//
// We compile the body once per operation id, with the runtime API destructured
// from the injected `runtime` argument.

import type { AnswerState, Operation } from "../types";
import { evalRule } from "./rule";
import { createRuntime, type RuntimeApi, type RuntimeOptions } from "./runtime";

const API_NAMES = [
  "getValue",
  "setValue",
  "getQA",
  "getAnswerCode",
  "setAnswer",
  "getAnswerVariable",
  "getAnswerLabel",
  // Flame Cells calls these two; Natural Draft never did. A helper missing from this
  // list is a ReferenceError inside the op script, caught per-op and logged, so the
  // whole operation silently does nothing.
  "getAnswerName",
  "getOrderField",
  // Pilot declares this in one operation and calls it from ANOTHER, which NetSuite
  // tolerates (one shared scope, declarations hoisted) and cpq2's per-operation IIFE
  // does not. Without it the ASSEMBLY_FULL branch threw and SALES_DESCRIPTION - set on
  // the very next line - was never written. The operation that declares its own copy
  // still shadows this parameter, so nothing changes for it.
  "cleanNozzleLabel",
  "getInputValue",
  "getSelectInternalId",
  "getQTable",
  "getTable",
  "getQTableCell",
  "trySet",
  "reset",
  "scratchpad",
  "getOrderData",
  "getRuleset",
  "getLabel",
  "recalcRulesets",
  "createRecord",
  "updateRecordFields",
  "getData",
] as const;

const compiledCache = new Map<string, (api: RuntimeApi) => void>();

function compileOperation(op: Operation): (api: RuntimeApi) => void {
  const cached = compiledCache.get(op.id);
  if (cached) return cached;
  // Wrap the script body in an IIFE that takes the runtime helpers as
  // PARAMETERS (not const). Many CPQ scripts redefine helpers like
  // `function trySet(...)` or `function getQA(...)` at script scope —
  // function declarations cleanly shadow parameters but would clash with
  // a const declaration (SyntaxError → silent no-op).
  const params = API_NAMES.join(", ");
  const args = API_NAMES.map((n) => `__api.${n}`).join(", ");
  const wrapped = `(function(${params}) {\n${op.custrecord_cpqc_code_exec}\n}).call(null, ${args});`;
  let fn: (api: RuntimeApi) => void;
  try {
    const body = new Function("__api", wrapped) as (api: RuntimeApi) => void;
    fn = body;
  } catch (err) {
    console.error(`[op ${op.id} ${op.name}] compile error:`, err);
    fn = () => {};
  }
  compiledCache.set(op.id, fn);
  return fn;
}

export type RunResult = {
  state: ReturnType<typeof createRuntime>;
  ran: Array<{ id: string; name: string; ok: boolean; error?: string }>;
};

// Run-once ops whose run only COUNTS once they have written something. "Set hood size" is
// gated on Hood Size being unanswered, which is already true at startup, before a housing
// exists: it found no Cell OD, did nothing, and was spent, so Hood Size never filled in once a
// housing was picked (Kevin, 2026-10-22: "the hood size is empty"). Its own rule stops it once
// Hood Size is set, so retrying it until it writes cannot overwrite a choice. Not for the
// general case: Clear Qtable writes nothing on an empty table and must still be spent.
const RUN_ONCE_WHEN_IT_WRITES = new Set(["SET_HOODSIZE"]);

/** Whether marking every run-once op done in bulk (a load, the prediction switch) should spend
 *  this one. Not the RUN_ONCE_WHEN_IT_WRITES ops: switching the prediction on and off again
 *  took away the predicted Hood Size and left nothing able to fill it back in. */
export function spentWhenMarkedDone(op: Operation): boolean {
  return (
    /SUBMITTED\/SUBMITTED/.test(String(op.custrecord_cpqc_operation_parsed_rule ?? "")) &&
    !RUN_ONCE_WHEN_IT_WRITES.has(String(op.custrecord_cpqc_operation_code ?? ""))
  );
}

/** The questions a run-once op sets, read from its script (setAnswer / setValue calls). */
function questionsSetBy(op: Operation): string[] {
  const code = String((op as { custrecord_cpqc_code_exec?: unknown }).custrecord_cpqc_code_exec ?? "");
  return [...new Set([...code.matchAll(/set(?:Answer|Value)\(\s*['"]([A-Z0-9_]+)['"]/g)].map((m) => m[1]))];
}

/**
 * Re-arm the spent run-once ops whose every question is blank again, so the next pass fills
 * them in as it would on a fresh form. For switching the prediction OFF: switching it on spends
 * every run-once op, the housing defaults included, before a housing exists, and switching off
 * takes the predicted values back out, which left Hood Size, Neck Height and the face plate
 * blank for good (Kevin, 2026-10-22). An op with ANY of its questions still answered stays
 * spent, so nothing the user set is re-defaulted; ops that set no answer at all (Clear Qtable)
 * are never re-armed, so a hand-typed housing row is not wiped.
 */
export function rearmBlankRunOnceOps(operations: Operation[], scratchpad: Record<string, unknown>, answers: AnswerState): void {
  const ran = scratchpad._ranOnce;
  if (!(ran instanceof Set)) return;
  const answered = new Set<string>();
  for (const [k, v] of Object.entries(answers)) {
    if (v === true || (typeof v === "string" && v.trim() !== "") || typeof v === "number") answered.add(k.slice(0, k.indexOf("/")));
  }
  for (const op of operations) {
    if (!ran.has(op.id) || !/SUBMITTED\/SUBMITTED/.test(String(op.custrecord_cpqc_operation_parsed_rule ?? ""))) continue;
    const qs = questionsSetBy(op);
    if (qs.length && qs.every((q) => !answered.has(q))) ran.delete(op.id);
  }
}

export function runOperations(
  operations: Operation[],
  runtimeOpts: RuntimeOptions,
  // Extra "what-if" overlay applied to answers during rule evaluation only —
  // e.g. {"CALCULATE2/CALCULATE2": true} to simulate the button being pressed.
  ruleOverlay: AnswerState = {}
): RunResult {
  const ordered = [...operations].sort(
    (a, b) =>
      Number(a.custrecord_cpqc_operation_sequence || 0) -
      Number(b.custrecord_cpqc_operation_sequence || 0)
  );

  const runtime = createRuntime(runtimeOpts);
  const ran: RunResult["ran"] = [];
  const skipped: Array<{ id: string; name: string; rule: string }> = [];

  // Some scripts (notably "Default Startup O and hinge type") are meant to
  // seed defaults once per session, then leave the user's choices alone.
  // CPQ tags these by referencing __["SUBMITTED/SUBMITTED"] in their rule
  // (so they re-run after submission). In our runtime SUBMITTED never flips,
  // so we'd re-fire defaults on every Calculate — wiping the user's UPSIZE,
  // BURNER_QTY, GAS_TYPE, etc. Treat them as "run once" via scratchpad.
  const ranOnce = (runtime.scratchpad._ranOnce ??= new Set<string>()) as Set<string>;
  function isRunOnce(op: Operation): boolean {
    return /SUBMITTED\/SUBMITTED/.test(op.custrecord_cpqc_operation_parsed_rule ?? "");
  }

  for (const op of ordered) {
    const rule = op.custrecord_cpqc_operation_parsed_rule;
    // Empty rule == "always run" (CPQ reactive script). Non-empty rules are
    // evaluated against runtime.answers ∪ overlay.
    if (rule) {
      const evalState = { ...runtime.answers, ...ruleOverlay };
      if (!evalRule(rule, evalState)) {
        skipped.push({ id: op.id, name: op.name, rule });
        continue;
      }
    }
    // Skip if already-run startup default.
    if (isRunOnce(op) && ranOnce.has(op.id)) {
      skipped.push({ id: op.id, name: op.name, rule: "(already ran once)" });
      continue;
    }

    const fn = compileOperation(op);
    const writesToCount = RUN_ONCE_WHEN_IT_WRITES.has(String(op.custrecord_cpqc_operation_code ?? ""));
    const answersBefore = writesToCount ? JSON.stringify(runtime.answers) : "";
    // While the MAPPING operation builds the encoded string, hidden user-editable
    // fields read as null (their held values stay in answers). Scoped to this op
    // only so other operations still see the real values.
    const isMapping = op.custrecord_cpqc_operation_code === "MAPPING";
    if (isMapping) runtime.setMappingMode(true);
    try {
      fn(runtime.api);
      ran.push({ id: op.id, name: op.name, ok: true });
      // A diagnostic hook for the browser harnesses: which operation changed what. Never set
      // in production; scripts/predict-fieldwise/browser installs it from an init script.
      const trace = (globalThis as { __cpq2OpTrace?: (id: string, name: string, answers: AnswerState) => void }).__cpq2OpTrace;
      if (typeof trace === "function") trace(op.id, op.name, runtime.answers as AnswerState);
      if (isRunOnce(op) && (!writesToCount || JSON.stringify(runtime.answers) !== answersBefore)) ranOnce.add(op.id);
      if (import.meta.env?.DEV && op.custrecord_cpqc_operation_code === "FLAME_CELL_SIZING") {
        console.log(
          "[CPQ] after FLAME_CELL_SIZING — tables.BURNER_TYPE =",
          JSON.parse(JSON.stringify(runtime.tables.BURNER_TYPE ?? []))
        );
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error(`[op ${op.id} ${op.name}] runtime error:`, err);
      ran.push({ id: op.id, name: op.name, ok: false, error: msg });
    } finally {
      if (isMapping) runtime.setMappingMode(false);
    }
  }

  if (import.meta.env?.DEV) {
    const flame = ran.find((r) => r.name === "Flame Cell Sizing");
    const flameSkipped = skipped.find((s) => s.name === "Flame Cell Sizing");
    console.log("[CPQ] FLAME_CELL_SIZING:", flame ?? `SKIPPED (rule: ${flameSkipped?.rule})`);
    console.log("[CPQ] ran ops:", ran.map((r) => r.name));
    console.log("[CPQ] skipped ops:", skipped.map((s) => `${s.name} (rule=${s.rule.slice(0, 40)})`));
  }

  return { state: runtime, ran };
}
